/**
 * Shared adapter-header -> FIELD_OPTIONS-key resolution.
 *
 * Both the API's confirm-mapping validation (import-mapping.helpers.ts) and the web
 * mapping-phase wizard (mapping-phase-wizard-helpers.ts) need to translate an adapter's
 * `requiredColumns`/`optionalColumns` (NOSNIKI_HEADERS bracket notation, e.g.
 * 'Szerokość [m]') into FIELD_OPTIONS keys (e.g. 'width_m'). This was previously
 * implemented twice with slightly different behavior — this is the single source of truth.
 */
import { FIELD_OPTIONS } from '../schemas/import-job-field-options.js';
import { XLSX_TEMPLATE_TRANSLATION } from './xlsx-template-translation.js';

/**
 * Resolves adapter header columns to FIELD_OPTIONS keys.
 *
 * Primary lookup: NOSNIKI_HEADER -> XLSX_TEMPLATE_TRANSLATION DB column -> FIELD_OPTIONS key.
 *   Works when FIELD_OPTIONS.key === DB column (width_m, height_m, material, lighting, …).
 * Fallback lookup: NOSNIKI_HEADER -> FIELD_OPTIONS label — covers cases where the adapter
 *   column string exactly matches a FIELD_OPTIONS label (mural flags, common Polish headers),
 *   for labels whose punctuation differs from the DB-column translation (parentheses vs.
 *   brackets for units).
 *
 * `knownFieldKeys` restricts which FIELD_OPTIONS keys are eligible via the primary lookup;
 * defaults to every FIELD_OPTIONS key when omitted.
 */
export function resolveFieldKeysFromAdapterColumns(
  columns: readonly string[],
  knownFieldKeys: Set<string> = new Set(FIELD_OPTIONS.map((f) => f.key)),
): Set<string> {
  const headerToFieldKeyViaTranslation = new Map<string, string>();
  for (const [header, entry] of Object.entries(XLSX_TEMPLATE_TRANSLATION)) {
    if (knownFieldKeys.has(entry.column)) {
      headerToFieldKeyViaTranslation.set(header, entry.column);
    }
  }
  const labelToKey = new Map(FIELD_OPTIONS.map((f) => [f.label, f.key]));

  const keys = new Set<string>();
  for (const col of columns) {
    const key = headerToFieldKeyViaTranslation.get(col) ?? labelToKey.get(col);
    if (key !== undefined) keys.add(key);
  }
  return keys;
}
